<script lang="ts" setup>
import type { TooltipInstance, UseTooltipProps, } from "element-plus"
import type { Props, } from "./types.ts"
import { useFormSize, useNamespace, } from "element-plus"
import { isUndefined, } from "lodash-es"
import "element-plus/es/components/text/style/index"

defineOptions({
  name: "ElText",
},)

const props = withDefaults(defineProps<Props>(), {
  type: "",
  size: "",
  tag: "span",
  center: true,
  rawContent: false,
  truncated: false,
  lineHeight: "normal",
},)

const defaultTooltipProps: Partial<UseTooltipProps> = {
  rawContent: true,
  showArrow: true,
  enterable: true,
  teleported: true,
  showAfter: 300,
  hideAfter: 100,
  offset: 12,
  placement: "bottom",
  effect: "dark",
  trigger: "hover",
}

const bindTooltipProps = computed<Partial<UseTooltipProps>>(() => {
  return {
    ...defaultTooltipProps,
    ...props.tooltipProps,
  }
},)

const textRef = ref<HTMLElement>()
const { height, } = useElementSize(textRef,)
const textHeight = ref()

const textSize = useFormSize()
const ns = useNamespace("text",)

const textKls = computed(() => [
  ns.b(),
  ns.m(props.type,),
  ns.m(textSize.value,),
  ns.is("truncated", props.truncated,),
  ns.is("line-clamp", !isUndefined(props.lineClamp,),),
],)

const attrs = useAttrs()

const content = ref("",)
const tooltipRef = ref<TooltipInstance>()
const tooltipVisible = ref(false,)

async function bindTitle() {
  await nextTick()
  let shouldAddTitle = false
  content.value = textRef.value?.textContent || ""
  if (props.truncated) {
    const width = textRef.value?.offsetWidth
    const scrollWidth = textRef.value?.scrollWidth
    if (width && scrollWidth && scrollWidth > width) {
      shouldAddTitle = true
    }
  } else if (!isUndefined(props.lineClamp,)) {
    const height = textRef.value?.offsetHeight
    const scrollHeight = textRef.value?.scrollHeight
    if (height && scrollHeight && scrollHeight > height) {
      shouldAddTitle = true
    }
    if (textRef.value) {
      const lineHeight = Number.parseFloat(getComputedStyle(textRef.value,).lineHeight,)
      const maxLine = Number.parseFloat(props.lineClamp.toString(),)
      const maxHeight = maxLine * lineHeight
      if (textRef.value.scrollHeight > maxHeight && content.value) {
        shouldAddTitle = true
      }
    }
  }
  tooltipVisible.value = shouldAddTitle
}

watchEffect(() => {
  if (height.value && content.value) {
    nextTick(() => {
      textHeight.value = height.value
    },)
  }
},)

const { copy, } = useClipboard()
function handleCopy() {
  if (props.rawContent) {
    copy(props.rawContent,)
  } else {
    copy(content.value,)
  }
  ElMessage.success("Copy success",)
}

onMounted(bindTitle,)
onUpdated(bindTitle,)
</script>

<template>
  <Component
    :is="tag"
    ref="textRef"
    :class="textKls"
    :style="{ '-webkit-line-clamp': lineClamp, 'lineHeight': lineClamp && center ? lineHeight : `${textHeight}px` }"
    v-bind="{ ...attrs }"
  >
    <Icon
      v-if="props.copyable && (content || props.rawContent)"
      class="mr-1 cursor-pointer"
      icon="ep:copy-document"
      @click.stop="handleCopy"
    />
    <slot v-if="!props.rawContent" />
    <div v-if="props.rawContent" v-html="props.rawContent" />
    <ElTooltip
      v-if="tooltipVisible"
      ref="tooltipRef"
      :content="props.rawContent ? props.rawContent : content"
      :popper-options="{
        modifiers: [
          {
            name: 'computeStyles',
            options: {
              adaptive: false,
              enabled: false,
            },
          },
        ],
      }"
      :virtual-ref="textRef"
      v-bind="{ ...bindTooltipProps }"
      virtual-triggering
    >
      <template #content>
        <slot name="content" />
      </template>
    </ElTooltip>
  </Component>
</template>

<style lang="scss" scoped>

</style>
